import React, { useContext, useEffect, useState } from 'react';
import { OrderContext } from '../App';
import { fetchOrderDetails, estTimeCalc, reverseGeocode } from '../services/api';
import { COMPLETED_STATUSES, formatDistanceKm, getOrderStatusLabel } from '../utils/orderDisplay';
import { getOrderStatusGroup } from '../utils/orderStatusGroups';
import OrderStatusGrid from './OrderStatusGrid';
import OrderTimeline from './OrderTimeline';
import './OrderExtraInfo.css';

/**
 * Order details panel: timeline + ETA driven by backend routeWindow.phase.
 */
function OrderExtraInfo() {
    const {
        selectedOrder, instance, coordinates, etaInfo, setEtaInfo,
        setCurrentLocation, selectedClientId, setRouteWindow, setRouteCoordinates
    } = useContext(OrderContext);
    const [travelData, setTravelData] = useState({
        travelHistory: [],
        eta: '',
        estimatedArrival: '',
        status: '',
        orderId: '',
        rawDetails: [],
        routeWindow: null
    });
    const [loadingOrderDetails, setLoadingOrderDetails] = useState(false);
    const [loadingEta, setLoadingEta] = useState(false);

    useEffect(() => {
        if (!selectedOrder) return;

        const isComplete = COMPLETED_STATUSES.includes(selectedOrder.Status);
        const isPrePickup = getOrderStatusGroup(selectedOrder.Status) === 'pre-pickup';

        // Wipe prior order ETA so "Complete" never sticks onto ASSGN / in-progress loads.
        setTravelData({
            travelHistory: [],
            eta: '',
            estimatedArrival: isComplete ? 'Complete' : '',
            status: '',
            orderId: selectedOrder.order_id,
            rawDetails: [],
            routeWindow: null
        });
        if (setEtaInfo) setEtaInfo(null);
        setCurrentLocation && setCurrentLocation(null);
        if (setRouteWindow) setRouteWindow(null);
        if (setRouteCoordinates) setRouteCoordinates([]);
        setLoadingOrderDetails(true);
        setLoadingEta(true);

        let cancelled = false;

        fetchOrderDetails(selectedOrder.order_id, instance, selectedClientId)
            .then(data => {
                if (cancelled) return;
                const window = data?.routeWindow || null;
                if (setRouteWindow) setRouteWindow(window);
                if (setRouteCoordinates) setRouteCoordinates(data?.routeCoordinates || []);

                const phase = window?.phase;
                let estimatedArrival = '';
                if (isComplete || phase === 'delivered') {
                    estimatedArrival = 'Complete';
                }

                setTravelData(prev => ({
                    ...prev,
                    ...data,
                    routeWindow: window,
                    estimatedArrival
                }));

                // ETA only while freight is loaded (backend phase).
                if (phase === 'loaded') {
                    runEta();
                } else {
                    setLoadingEta(false);
                }
            })
            .catch(() => {
                if (cancelled) return;
                if (setRouteWindow) setRouteWindow(null);
                if (setRouteCoordinates) setRouteCoordinates([]);
                setTravelData({
                    travelHistory: [],
                    eta: '',
                    estimatedArrival: '',
                    status: '',
                    orderId: selectedOrder.order_id,
                    rawDetails: [],
                    routeWindow: null
                });
                setLoadingEta(false);
            })
            .finally(() => {
                if (!cancelled) setLoadingOrderDetails(false);
            });

        const runEta = async () => {
            try {
                let pickup = coordinates.pickup;
                let delivery = coordinates.delivery;

                if (!pickup && selectedOrder?.origin_address) {
                    const loc = await reverseGeocode(selectedOrder.origin_address, instance);
                    if (loc) pickup = [loc.lat, loc.lon];
                }
                if (!delivery && selectedOrder?.destination_address) {
                    const loc = await reverseGeocode(selectedOrder.destination_address, instance);
                    if (loc) delivery = [loc.lat, loc.lon];
                }

                if (!pickup || !delivery) {
                    setTravelData(prev => ({
                        ...prev,
                        estimatedArrival: prev.estimatedArrival === 'Complete' ? '' : prev.estimatedArrival
                    }));
                    setLoadingEta(false);
                    return;
                }

                const etaData = await estTimeCalc(
                    pickup[0],
                    pickup[1],
                    delivery[0],
                    delivery[1],
                    selectedOrder?.VehicleNum || '',
                    instance
                );

                if (etaData) {
                    setEtaInfo(etaData);
                    if (etaData.proximity && setCurrentLocation) {
                        setCurrentLocation(etaData.proximity);
                    }
                    const arrivalTime = new Date(Date.now() + (etaData.duration_min * 60000));
                    setTravelData(prev => ({
                        ...prev,
                        estimatedArrival: formatDateTime(arrivalTime)
                    }));
                } else {
                    setTravelData(prev => ({
                        ...prev,
                        estimatedArrival: prev.estimatedArrival === 'Complete' ? '' : (prev.estimatedArrival || '')
                    }));
                }
            } catch {
                setTravelData(prev => ({
                    ...prev,
                    estimatedArrival: prev.estimatedArrival === 'Complete' ? '' : prev.estimatedArrival
                }));
            } finally {
                setLoadingEta(false);
            }
        };

        // Pre-pickup list statuses never need FE ETA before details return.
        if (isComplete || isPrePickup) {
            setLoadingEta(false);
        }

        return () => { cancelled = true; };
    }, [selectedOrder, instance, selectedClientId, coordinates.pickup, coordinates.delivery, setEtaInfo, setCurrentLocation, setRouteWindow, setRouteCoordinates]);

    /** Format a Date for the ETA grid. */
    const formatDateTime = (date) => {
        if (!date) return '';
        return date.toLocaleString('en-US', {
            weekday: 'short',
            month: 'short',
            day: 'numeric',
            year: 'numeric',
            hour: 'numeric',
            minute: '2-digit',
            hour12: true
        });
    };

    const phase = travelData?.routeWindow?.phase;
    const distanceText = (COMPLETED_STATUSES.includes(selectedOrder?.Status) || phase === 'delivered')
        ? '0 km'
        : (phase === 'to_pickup' || phase === 'not_dispatched')
            ? '—'
            : (loadingEta ? '…' : formatDistanceKm(etaInfo));

    const etaDisplay = (COMPLETED_STATUSES.includes(selectedOrder?.Status) || phase === 'delivered')
        ? 'Complete'
        : (phase === 'to_pickup' || phase === 'not_dispatched')
            ? '—'
            : loadingEta
                ? 'Loading…'
                : (travelData?.estimatedArrival || '—');

    return (
        <div className="order-extra-info">
            <h2>Order Details</h2>
            <OrderStatusGrid
                key={selectedOrder?.order_id}
                className="fade-in"
                orderId={selectedOrder?.order_id || '—'}
                status={getOrderStatusLabel(selectedOrder?.Status)}
                eta={etaDisplay}
                distance={distanceText}
                truck={selectedOrder?.VehicleNum || '—'}
                trailer={selectedOrder?.trailer || '—'}
            />

            {loadingOrderDetails ? (
                <div className="loading-spinner fade-in">
                    Loading order details...
                </div>
            ) : (
                <OrderTimeline
                    className="travel-history-container"
                    itemClass="fade-up"
                    events={travelData?.travelHistory}
                    originAddress={selectedOrder?.origin_address}
                    destinationAddress={selectedOrder?.destination_address}
                    shipperTemp={travelData?.shipper_temp ?? selectedOrder?.shipper_temp}
                    consigneeTemp={travelData?.consignee_temp ?? selectedOrder?.consignee_temp}
                    haulTempMin={selectedOrder?.haul_temp_f_min}
                    haulTempMax={selectedOrder?.haul_temp_f_max}
                />
            )}
        </div>
    );
}

export default OrderExtraInfo;
